// Dry-run viz preview — hits the server's POST /viz/preview endpoint with a
// hypothetical compound swap and returns a VizPayload-shaped response that
// the workbench plugs into its existing aiVizOverride pipeline (orange
// Sensory Radar + Flavor Space). No DB writes; per-click latency ~100 ms.
//
// Server: apps/nos/server/src/controllers/.../viz/preview.ts

import { serverURL } from "@/utils/serverURL";
import type { VizPayload } from "@/services/workbench_api";

export type PreviewVizArgs = {
  flavorId: string;
  formulaId: number;
  versionId: number;
  /** Source compound being replaced. The server resolves the ffvi by
   *  (versionId, ndbTable, rowid); concentrations don't enter the
   *  preview compute. */
  from: { ndbTable: "compound" | "food"; rowid: number };
  /** Replacement candidates. Empty array = drop preview (just remove
   *  the source row, append nothing). 1 entry = single swap. 2 entries =
   *  combo. */
  toItems: Array<{ ndbTable: "compound" | "food"; rowid: number }>;
};

export class PreviewVizApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(`preview viz ${status}`);
    this.name = "PreviewVizApiError";
    this.status = status;
    this.body = body;
  }
}

export async function fetchPreviewViz(
  args: PreviewVizArgs,
  signal: AbortSignal,
): Promise<VizPayload> {
  const res = await fetch(
    serverURL`/me/flavor/${args.flavorId}/formulas/${args.formulaId}/versions/${args.versionId}/viz/preview`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ from: args.from, toItems: args.toItems }),
      signal,
    },
  );
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* ignore — body may not be JSON */
    }
    throw new PreviewVizApiError(res.status, body);
  }
  return (await res.json()) as VizPayload;
}
